import { useState, FormEvent, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LoginIcon from "@mui/icons-material/Login";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";

import { baseUrl } from "../../constants";
import generateFirebaseToken from "../../notifications/firebase-token-generate";

const SignIn = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // form
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);

  // ux
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [error, setError] = useState("");

  // derived
  const isDisabled = useMemo(
    () => loading || !username.trim() || !password.trim(),
    [loading, username, password]
  );

  useEffect(() => {
    const accessToken =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken");
    if (accessToken) navigate("/home");
  }, [navigate]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isDisabled) return;

    setLoading(true);
    setError("");

    try {
      const { data } = await axios.post(
        baseUrl + "/auth/login", 
        {
          username: username.trim(),
          password,
        },
        { 
          timeout: 10000
        }
      );

      const access_token: string = data?.access_token;
      if (!access_token) {
        throw new Error("Invalid server response. Missing access token.");
      }

      // Persist access token
      if (remember) {
        localStorage.setItem("accessToken", access_token);
      } else {
        sessionStorage.setItem("accessToken", access_token);
      }
      
      // Notify app that token changed
      window.dispatchEvent(new Event("auth-token-changed"));

      // Generate Firebase token in background
      generateFirebaseToken().catch((err) => {
        console.error("Firebase token generation failed:", err);
      });

      navigate("/home");
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Could not sign in. Please try again.";
      setError(Array.isArray(message) ? message[0] : message);
      setSnackbarOpen(true);
      console.error("Sign in error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "grid",
        placeItems: "center",
        background:
          theme.palette.mode === "dark"
            ? "radial-gradient(1200px 600px at 10% -10%, rgba(93,176,255,0.08), transparent), linear-gradient(180deg, rgba(255,255,255,0.02), transparent)"
            : "radial-gradient(1200px 600px at 10% -10%, rgba(93,176,255,0.20), transparent), linear-gradient(180deg, #F7FAFF, #ECF3FF)",
        px: 2,
      }}
    >
      <Grid container maxWidth="md" alignItems="stretch">
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              p: { xs: 3, sm: 4, md: 6 },
              backdropFilter: "blur(10px)",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(20,22,25,0.6)"
                  : "rgba(255,255,255,0.7)",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)"
              }`,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                mb: 2,
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Typography variant="h4" fontWeight={800} lineHeight={1.2}>
                  Welcome back
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Sign in to continue to your workspace.
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.9 }}>
                  v1.0 • secure sign-in
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Username"
                    placeholder="your.username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    autoFocus
                    inputProps={{ maxLength: 120 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineOutlinedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    placeholder="••••••••"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    onKeyUp={(e: any) => {
                      const caps =
                        e?.getModifierState && e.getModifierState("CapsLock");
                      setCapsLock(Boolean(caps));
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword((p) => !p)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    helperText={
                      capsLock ? (
                        <Box display="inline-flex" alignItems="center" gap={0.5} color="warning.main">
                          <ErrorOutlineOutlinedIcon fontSize="small" /> Caps Lock is on
                        </Box>
                      ) : " "
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={1}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={remember}
                          onChange={(e) => setRemember(e.target.checked)}
                        />
                      }
                      label="Keep me signed in"
                    />
                    <Box>
                      <Link
                        to="/forgot-password"
                        style={{
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                          textDecoration: "none",
                        }}
                      >
                        Forgot password?
                      </Link>
                    </Box>
                  </Box>
                </Grid>

                {error ? (
                  <Grid item xs={12}>
                    <Alert severity="error" variant="outlined">
                      {error}
                    </Alert>
                  </Grid>
                ) : null}

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={isDisabled}
                    startIcon={!loading ? <LoginIcon /> : undefined}
                    sx={{
                      py: 1.25,
                      borderRadius: 2,
                      textTransform: "none",
                      fontSize: 16,
                      fontWeight: 700,
                    }}
                  >
                    {loading ? <CircularProgress size={22} /> : "Sign In"}
                  </Button>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    mt={0.5}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Don&apos;t have an account?
                    </Typography>
                    <Link
                      to="/signup"
                      style={{
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        textDecoration: "none",
                      }}
                    >
                      Create one
                    </Link>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Extra footer info */}
            <Box
              mt={4}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
            >
              <Typography variant="caption" color="text.secondary">
                By signing in, you agree to our{" "}
                <Link
                  to="/terms"
                  style={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    textDecoration: "none",
                  }}
                >
                  Terms
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy"
                  style={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    textDecoration: "none",
                  }}
                >
                  Privacy Policy
                </Link>
                .
              </Typography>
              <Typography variant="caption" color="text.secondary">
                © {new Date().getFullYear()} Your Company. All rights reserved.
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Snackbar for error message */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: "100%", fontSize: "16px", fontWeight: "bold" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SignIn;
